import { CheckOutlined, DeleteOutlined, ExportOutlined, LinkOutlined } from '@ant-design/icons';
import type { ButtonProps } from 'antd';
import { Button, Input, Popover, Space, Tooltip } from 'antd';
import type { Dispatch, FC, KeyboardEvent, MouseEvent, SetStateAction } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';

import { useTiptapEditor } from '../../use-tiptap-editor';
import type { UseLinkPopoverConfig } from './use-link-popover';
import { useLinkPopover } from './use-link-popover';

interface OwnProps extends Omit<ButtonProps, 'type'>, UseLinkPopoverConfig {
  onOpenChange?: (isOpen: boolean) => void;
  autoOpenOnLinkActive?: boolean;
}

interface LinkMainProps {
  url: string;
  setUrl: Dispatch<SetStateAction<string | null>>;
  setLink: () => void;
  removeLink: () => void;
  openLink: () => void;
  isActive: boolean;
}

const LinkButton = forwardRef<HTMLButtonElement, ButtonProps>(function LinkButton({ className, ...props }, ref) {
  return (
    <Tooltip title="Link">
      <Button type="default" className={className} ref={ref} {...props} />
    </Tooltip>
  );
});

const LinkMain: FC<LinkMainProps> = ({ url, setUrl, setLink, removeLink, openLink, isActive }) => {
  const handleEnterKey = (event: KeyboardEvent) => {
    event.preventDefault();
    setLink();
  };

  return (
    <Space direction="vertical" style={{ width: 260 }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Input
          type="url"
          placeholder="Paste a link..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onPressEnter={handleEnterKey}
          addonBefore={<LinkOutlined />}
          /* eslint-disable-next-line jsx-a11y/no-autofocus */
          autoFocus
        />
        <Space.Compact>
          <Button
            type="text"
            onClick={setLink}
            title="Apply link"
            icon={<CheckOutlined />}
            disabled={!url && !isActive}
          />
          <Button
            type="text"
            onClick={openLink}
            title="Open in new window"
            icon={<ExportOutlined />}
            disabled={!url && !isActive}
          />
          <Button
            type="text"
            danger
            onClick={removeLink}
            title="Remove link"
            icon={<DeleteOutlined />}
            disabled={!url || !isActive}
          />
        </Space.Compact>
      </Space>
    </Space>
  );
};

const LinkPopover = forwardRef<HTMLButtonElement, OwnProps>(function LinkPopover(
  {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onSetLink,
    onOpenChange,
    autoOpenOnLinkActive = true,
    onClick,
    ...buttonProps
  },
  ref,
) {
  const { editor } = useTiptapEditor(providedEditor);
  const [isOpen, setIsOpen] = useState(false);

  const { isVisible, canSet, isActive, url, setUrl, setLink, removeLink, openLink, label, Icon } = useLinkPopover({
    editor,
    hideWhenUnavailable,
    onSetLink,
  });

  const handleOnOpenChange = useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
    },
    [onOpenChange],
  );

  const handleSetLink = useCallback(() => {
    setLink();
    setIsOpen(false);
  }, [setLink]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      setIsOpen(!isOpen);
    },
    [onClick, isOpen],
  );

  useEffect(() => {
    if (autoOpenOnLinkActive && isActive) {
      setIsOpen(true);
    }
  }, [autoOpenOnLinkActive, isActive]);

  if (!isVisible) {
    return null;
  }

  const popoverContent = (
    <LinkMain
      url={url}
      setUrl={setUrl}
      setLink={handleSetLink}
      removeLink={removeLink}
      openLink={openLink}
      isActive={isActive}
    />
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOnOpenChange} trigger="click" content={popoverContent}>
      <LinkButton
        disabled={!canSet}
        aria-label={label}
        aria-pressed={isActive}
        onClick={handleClick}
        icon={<Icon className="tiptap-button-icon" />}
        {...buttonProps}
        ref={ref}
      />
    </Popover>
  );
});

export default LinkPopover;
